import React from 'react';
import { Link } from 'react-router-dom';

const CONTACT_EMAIL = 'alvin@alvinahmad.dev';
const LAST_UPDATED = 'June 18, 2026';

const CONTENT = {
  privacy: {
    title: 'Privacy Policy',
    intro:
      'This Privacy Policy explains what information is collected when you use this website, how it is used, and the choices you have. By using the site, you agree to the practices described here.',
    sections: [
      {
        h: 'Information We Collect',
        body: [
          'Contact form: when you reach out, we collect the name, email, phone number, company, and website you provide. These are sent by email through EmailJS so we can reply to you.',
          'Project feedback: if you leave feedback on a project, we store your name, email (optional), message, and rating in our database (Supabase).',
          'Likes: a random, anonymous identifier is stored in your browser (localStorage) so a project is not liked twice from the same device. It is not linked to your identity.',
          'Usage data: standard, aggregated analytics such as page views may be collected to understand how the site is used.',
        ],
      },
      {
        h: 'How We Use Your Information',
        body: [
          'To respond to your inquiries and discuss potential projects.',
          'To display project feedback you choose to submit.',
          'To operate, maintain, and improve the website.',
          'We do not sell your personal information.',
        ],
      },
      {
        h: 'Third-Party Services',
        body: [
          'EmailJS — delivers contact-form messages. See emailjs.com/legal/privacy-policy.',
          'Supabase — stores feedback, likes, and project data. See supabase.com/privacy.',
          'Vercel — hosts the website. See vercel.com/legal/privacy-policy.',
        ],
      },
      {
        h: 'Cookies & Local Storage',
        body: [
          'This site uses browser localStorage to remember your language preference and to prevent duplicate likes. It does not use advertising or cross-site tracking cookies.',
        ],
      },
      {
        h: 'Data Retention & Your Rights',
        body: [
          'We keep submitted information only as long as needed for the purposes above.',
          `You may request access to, correction of, or deletion of your personal data by emailing ${CONTACT_EMAIL}.`,
        ],
      },
      {
        h: 'Contact',
        body: [`Questions about this policy? Email ${CONTACT_EMAIL}.`],
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    intro:
      'These Terms govern your use of this website. By accessing or using the site, you agree to be bound by them. If you do not agree, please do not use the site.',
    sections: [
      {
        h: 'Use of the Site',
        body: [
          'This website is a personal portfolio showcasing software development and design work and a way to get in touch about projects.',
          'You agree to use the site lawfully and not to disrupt, damage, or attempt to gain unauthorized access to it or its data.',
        ],
      },
      {
        h: 'Intellectual Property',
        body: [
          'Unless stated otherwise, the site design, text, and original graphics are owned by Alvin Ahmad and may not be reused without permission.',
          'Showcased project work and client logos remain the property of their respective owners and are displayed for portfolio purposes.',
        ],
      },
      {
        h: 'User Submissions',
        body: [
          'When you submit feedback, you grant permission to display it on the site. Do not submit unlawful, infringing, or offensive content. We may remove any submission at our discretion.',
        ],
      },
      {
        h: 'Third-Party Links',
        body: [
          'The site may link to external websites. We are not responsible for the content or practices of those sites.',
        ],
      },
      {
        h: 'Disclaimer & Limitation of Liability',
        body: [
          'The site is provided "as is" without warranties of any kind. To the maximum extent permitted by law, we are not liable for any damages arising from your use of the site.',
        ],
      },
      {
        h: 'Changes & Governing Law',
        body: [
          'These Terms may be updated from time to time; the "last updated" date will reflect changes.',
          'These Terms are governed by the laws of the Republic of Indonesia.',
        ],
      },
      {
        h: 'Contact',
        body: [`Questions about these Terms? Email ${CONTACT_EMAIL}.`],
      },
    ],
  },
};

const labelStyle = {
  fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
  fontSize: '11px',
  fontWeight: 700,
  color: '#8E827C',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const LegalPage = ({ kind = 'privacy' }) => {
  const data = CONTENT[kind] || CONTENT.privacy;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--v2-color-offwhite)', padding: '120px 24px 96px' }}>
      <article style={{ maxWidth: '760px', margin: '0 auto' }}>
        <Link
          to="/"
          style={{
            ...labelStyle,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            textDecoration: 'none',
            marginBottom: '40px',
          }}
        >
          ← Back to home
        </Link>

        <div style={{ ...labelStyle, marginBottom: '16px' }}>Legal · Last updated {LAST_UPDATED}</div>

        <h1
          style={{
            fontFamily: "'Gellix', -apple-system, BlinkMacSystemFont, sans-serif",
            fontSize: 'clamp(40px, 6vw, 64px)',
            fontWeight: 700,
            color: 'var(--v2-color-charcoal)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '0 0 24px',
          }}
        >
          {data.title}
        </h1>

        <p
          style={{
            fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
            fontSize: '18px',
            color: '#5A524C',
            lineHeight: 1.7,
            marginBottom: '48px',
          }}
        >
          {data.intro}
        </p>

        {data.sections.map((section, i) => (
          <section key={i} style={{ marginBottom: '40px' }}>
            <h2
              style={{
                fontFamily: "'Gellix', -apple-system, BlinkMacSystemFont, sans-serif",
                fontSize: '22px',
                fontWeight: 700,
                color: 'var(--v2-color-charcoal)',
                margin: '0 0 16px',
                letterSpacing: '-0.01em',
              }}
            >
              {section.h}
            </h2>
            {section.body.map((para, j) => (
              <p
                key={j}
                style={{
                  fontFamily: "'Gellix', 'Helvetica Neue', sans-serif",
                  fontSize: '16px',
                  color: '#5A524C',
                  lineHeight: 1.7,
                  margin: '0 0 12px',
                }}
              >
                {para}
              </p>
            ))}
          </section>
        ))}

        <div style={{ display: 'flex', gap: '24px', marginTop: '64px', paddingTop: '32px', borderTop: '1px solid #E2DFDD' }}>
          <Link to="/privacy" style={{ ...labelStyle, textDecoration: 'none' }}>Privacy</Link>
          <Link to="/terms" style={{ ...labelStyle, textDecoration: 'none' }}>Terms</Link>
        </div>
      </article>
    </div>
  );
};

export default LegalPage;
